/**
 * The bundled `Intl.DateTimeFormat` (formatjs, with the IANA rules of its release) in place of the browser's, and
 * `Date`'s toLocale…String with it: every zone, English and the browser's own language where it is French or
 * Spanish, and the computer's own clock as the default zone (the polyfill's own default is UTC, which would move
 * every time written without a zone). Fetched only by a browser whose rules are out of date: its files are their
 * own chunk, never in the first load.
 */
export async function loadIntlPolyfill(
  localZone: string | null
): Promise<void> {
  // The polyfill first: the zone and locale data register with it as they load.
  await import("@formatjs/intl-datetimeformat/polyfill-force.js");
  await Promise.all([
    import("@formatjs/intl-datetimeformat/add-all-tz.js"),
    import("@formatjs/intl-datetimeformat/locale-data/en.js"),
    ...languagesBeyondEnglish().map((language) => LOCALE_DATA[language]()),
  ]);
  const DateTimeFormat = Intl.DateTimeFormat as typeof Intl.DateTimeFormat & {
    __setDefaultTimeZone?: (zone: string) => void;
  };
  if (localZone) DateTimeFormat.__setDefaultTimeZone?.(localZone);
}

// Named one by one, so the bundler makes each its own file.
const LOCALE_DATA = {
  fr: () => import("@formatjs/intl-datetimeformat/locale-data/fr.js"),
  es: () => import("@formatjs/intl-datetimeformat/locale-data/es.js"),
} as const;

/** The browser's languages the clinic's dates may be written in, beyond English. */
function languagesBeyondEnglish(): (keyof typeof LOCALE_DATA)[] {
  const asked =
    typeof navigator === "undefined"
      ? []
      : navigator.languages?.length
        ? navigator.languages
        : [navigator.language];
  const languages = new Set(
    asked.map((tag) => String(tag).split("-")[0].toLowerCase())
  );
  return (Object.keys(LOCALE_DATA) as (keyof typeof LOCALE_DATA)[]).filter(
    (language) => languages.has(language)
  );
}
